import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectItem } from '@/components/ui/select';
import { ThemedText } from '@/components/themed-text';
import { ChargeException } from '@/types/charge-exception';
import { EXCEPTION_ACTION } from '@/types/exception-action';
import { useCreateChargeException, useUpdateChargeException } from '@/hooks/use-charge-exceptions';
import { getApiErrorMessage } from '@/utils/api-error';
import { useToastStore } from '@/store/toastStore';
import { format, isValid, parse } from 'date-fns';

type ChargeExceptionModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enrollmentId: string;
  mode: 'create' | 'edit';
  exception?: ChargeException | null;
  defaultDate?: Date;
  onSuccess?: () => void;
};

export function ChargeExceptionModal({
  open,
  onOpenChange,
  enrollmentId,
  mode,
  exception,
  defaultDate,
  onSuccess,
}: ChargeExceptionModalProps) {
  const { addToast } = useToastStore();
  const createMutation = useCreateChargeException(enrollmentId);
  const updateMutation = useUpdateChargeException(enrollmentId);

  const [originalDate, setOriginalDate] = useState(
    exception?.originalChargeDate
      ? format(new Date(exception.originalChargeDate), 'dd/MM/yyyy')
      : defaultDate
        ? format(defaultDate, 'dd/MM/yyyy')
        : '',
  );
  const [action, setAction] = useState<EXCEPTION_ACTION>(
    exception?.action || EXCEPTION_ACTION.SKIP,
  );
  const [newDueDate, setNewDueDate] = useState(
    exception?.newDueDate ? format(new Date(exception.newDueDate), 'dd/MM/yyyy') : '',
  );
  const [newAmount, setNewAmount] = useState(
    exception?.newAmount ? String(exception.newAmount) : '',
  );
  const [reason, setReason] = useState(exception?.reason || '');

  useEffect(() => {
    if (open) {
      setOriginalDate(
        exception?.originalChargeDate
          ? format(new Date(exception.originalChargeDate), 'dd/MM/yyyy')
          : defaultDate
            ? format(defaultDate, 'dd/MM/yyyy')
            : '',
      );
      setAction(exception?.action || EXCEPTION_ACTION.SKIP);
      setNewDueDate(
        exception?.newDueDate ? format(new Date(exception.newDueDate), 'dd/MM/yyyy') : '',
      );
      setNewAmount(exception?.newAmount ? String(exception.newAmount) : '');
      setReason(exception?.reason || '');
    }
  }, [open, exception, defaultDate]);

  const isPending = createMutation.isPending || updateMutation.isPending;

  const parseDateInput = (value: string) => {
    const parsed = parse(value, 'dd/MM/yyyy', new Date());
    return isValid(parsed) ? parsed : null;
  };

  const handleSubmit = async () => {
    if (!originalDate) {
      addToast('Informe a data original da cobrança.', 'error');
      return;
    }
    const parsedOriginalDate = parseDateInput(originalDate);
    if (!parsedOriginalDate) {
      addToast('Data original inválida. Use DD/MM/AAAA.', 'error');
      return;
    }

    if (!reason.trim()) {
      addToast('Adicione um motivo para a exceção.', 'error');
      return;
    }

    const payload: any = {
      originalChargeDate: format(parsedOriginalDate, 'yyyy-MM-dd'),
      action,
      reason,
    };

    if (action === EXCEPTION_ACTION.POSTPONE) {
      if (!newDueDate) {
        addToast('Informe a nova data de vencimento.', 'error');
        return;
      }
      const parsedNewDate = parseDateInput(newDueDate);
      if (!parsedNewDate) {
        addToast('Nova data inválida. Use DD/MM/AAAA.', 'error');
        return;
      }
      payload.newDueDate = format(parsedNewDate, 'yyyy-MM-dd');
      payload.newAmount = undefined;
    }

    if (action === EXCEPTION_ACTION.MODIFY_AMOUNT) {
      const amountValue = Number(newAmount);
      if (!newAmount || Number.isNaN(amountValue) || amountValue <= 0) {
        addToast('Informe um valor válido para o novo valor.', 'error');
        return;
      }
      payload.newAmount = amountValue;
      payload.newDueDate = undefined;
    }

    try {
      if (mode === 'create') {
        await createMutation.mutateAsync(payload);
      } else if (exception?.id) {
        await updateMutation.mutateAsync({ id: exception.id, payload });
      }
      addToast('Exceção salva com sucesso.', 'success');
      onSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      addToast(getApiErrorMessage(error, 'Não foi possível salvar a exceção de cobrança.'), 'error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !isPending && onOpenChange(value)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            <ThemedText className="font-bold text-xl">
              {mode === 'create' ? 'Adicionar exceção' : 'Editar exceção'}
            </ThemedText>
          </DialogTitle>
          <DialogDescription>
            <ThemedText className="text-sm text-foreground/60">
              Ajuste a cobrança para pausas, adiamentos ou alterações pontuais.
            </ThemedText>
          </DialogDescription>
        </DialogHeader>

        <View className="mt-4 gap-4">
          <View className="gap-2">
            <ThemedText className="font-medium text-sm">Data original da cobrança</ThemedText>
            <Input
              placeholder="DD/MM/AAAA"
              value={originalDate}
              onChangeText={setOriginalDate}
              keyboardType="numbers-and-punctuation"
            />
          </View>

          <View className="gap-2">
            <ThemedText className="font-medium text-sm">Ação</ThemedText>
            <Select
              value={action}
              onValueChange={(value) => setAction(value as EXCEPTION_ACTION)}
              placeholder="Selecione a ação"
            >
              <SelectItem value={EXCEPTION_ACTION.SKIP}>
                <ThemedText>Pular cobrança</ThemedText>
              </SelectItem>
              <SelectItem value={EXCEPTION_ACTION.POSTPONE}>
                <ThemedText>Adiamento</ThemedText>
              </SelectItem>
              <SelectItem value={EXCEPTION_ACTION.MODIFY_AMOUNT}>
                <ThemedText>Alterar valor</ThemedText>
              </SelectItem>
            </Select>
          </View>

          {action === EXCEPTION_ACTION.POSTPONE && (
            <View className="gap-2">
              <ThemedText className="font-medium text-sm">Nova data de vencimento</ThemedText>
              <Input
                placeholder="DD/MM/AAAA"
                value={newDueDate}
                onChangeText={setNewDueDate}
                keyboardType="numbers-and-punctuation"
              />
            </View>
          )}

          {action === EXCEPTION_ACTION.MODIFY_AMOUNT && (
            <View className="gap-2">
              <ThemedText className="font-medium text-sm">Novo valor (R$)</ThemedText>
              <Input
                placeholder="Ex: 150"
                value={newAmount}
                onChangeText={setNewAmount}
                keyboardType="decimal-pad"
              />
            </View>
          )}

          <View className="gap-2">
            <ThemedText className="font-medium text-sm">Motivo</ThemedText>
            <Textarea
              placeholder="Explique o motivo da exceção"
              value={reason}
              onChangeText={setReason}
              numberOfLines={4}
              className="min-h-[90px]"
            />
          </View>
        </View>

        <DialogFooter className="mt-6">
          <Button variant="outline" onPress={() => onOpenChange(false)} disabled={isPending}>
            <ThemedText>Cancelar</ThemedText>
          </Button>
          <Button onPress={handleSubmit} disabled={isPending}>
            <ThemedText>{mode === 'create' ? 'Adicionar' : 'Salvar'}</ThemedText>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
